import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Light-themed variant of the global Footer. Used by partner landing pages
 * that set `theme: "light"` (e.g. /p/ica). Mirrors the structure of the dark
 * Footer but with white background + dark text so the page reads as a single
 * light surface from top to bottom.
 */
export default function FooterLight() {
  const { t } = useLang();
  return (
    <footer data-testid="footer-light" className="bg-white border-t border-black/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-4">{t("footer_tag")}</span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A1A1A] leading-[1.1]">
              {t("footer_title")}<br /><span className="italic text-black/60">{t("footer_title_italic")}</span>
            </h2>
          </div>
          <a href="/#contact" data-testid="footer-request-access-btn-light" className="btn-primary px-10 py-4 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2 whitespace-nowrap">
            {t("nav_request")} <ArrowRight size={14} />
          </a>
        </div>
      </div>
      <div className="border-t border-black/10" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-1">
            <Link to="/" className="flex flex-col items-start mb-4">
              <span className="text-[#C5A059] font-semibold text-base tracking-[0.3em] uppercase font-['Outfit']">SERAVA</span>
              <span className="text-black/40 text-[9px] tracking-[0.25em] uppercase font-['Outfit'] font-light -mt-0.5">Sportsevents</span>
            </Link>
            <p className="text-black/50 font-['Outfit'] font-light text-sm leading-relaxed">{t("premium_tagline")}</p>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-['Outfit'] font-semibold mb-6">{t("footer_nav")}</h4>
            <ul className="space-y-3">
              {[{ label: t("nav_events"), href: "/#events" }, { label: t("nav_about"), href: "/#why-serava" }, { label: t("nav_how"), href: "/#how-it-works" }, { label: t("news_tag"), href: "/#newsletter" }].map((link) => (
                <li key={link.label}><a href={link.href} className="text-black/50 hover:text-[#C5A059] text-sm font-['Outfit'] font-light transition-colors">{link.label}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-['Outfit'] font-semibold mb-6">{t("footer_company")}</h4>
            <ul className="space-y-3">
              {[
                { label: t("about_serava"), slug: "om-serava" },
                { label: t("privacy_policy"), slug: "integritetspolicy" },
                { label: t("terms_of_service"), slug: "anvandarvillkor" },
              ].map((link) => (
                <li key={link.slug}>
                  <Link to={`/p/${link.slug}`} data-testid={`footer-light-link-${link.slug}`} className="text-black/50 hover:text-[#C5A059] text-sm font-['Outfit'] font-light transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div id="contact">
            <h4 className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-['Outfit'] font-semibold mb-6">{t("footer_contact")}</h4>
            <ul className="space-y-3 text-black/50 font-['Outfit'] font-light text-sm">
              <li>contact@seravagroup.com</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-black/10" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="text-black/30 text-xs font-['Outfit'] font-light">&copy; {new Date().getFullYear()} Serava Sports AB. {t("footer_rights")}</p>
        <div className="flex gap-6">
          {[
            { label: t("privacy"), slug: "integritet" },
            { label: t("terms"), slug: "villkor" },
            { label: t("cookies"), slug: "cookies" },
          ].map((link) => (
            <Link key={link.slug} to={`/p/${link.slug}`} data-testid={`footer-light-bottom-link-${link.slug}`} className="text-black/30 hover:text-black/60 text-xs font-['Outfit'] font-light transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}
